import { Link } from 'react-router-dom';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import QuoteForm from '../../../components/feature/QuoteForm';

export default function MaintenanceServices() {
  const services = [
    {
      title: 'Asphalt Sealcoating',
      description: 'Protect your pavement with professional sealcoating that extends lifespan and enhances appearance.',
      icon: 'ri-paint-brush-line',
    },
    {
      title: 'Crack Filling',
      description: 'Hot rubberized crack sealing prevents water infiltration and stops minor cracks from becoming major problems.',
      icon: 'ri-contrast-drop-2-line',
    },
    {
      title: 'Pavement Resurfacing',
      description: 'Overlay worn asphalt with fresh surface layer for renewed strength and smooth appearance.',
      icon: 'ri-refresh-line',
    },
    {
      title: 'Pothole Repair',
      description: 'Fast, permanent pothole patching using hot asphalt and proper compaction techniques.',
      icon: 'ri-tools-line',
    },
    {
      title: 'Line Striping',
      description: 'Professional parking lot striping, pavement markings, and ADA-compliant handicap spaces.',
      icon: 'ri-road-map-line',
    },
    {
      title: 'Preventive Maintenance',
      description: 'Scheduled maintenance programs that maximize pavement life and minimize costly repairs.',
      icon: 'ri-calendar-check-line',
    },
  ];

  const faqs = [
    {
      question: 'How often should I sealcoat my asphalt driveway in Lincoln NE?',
      answer: 'Sealcoat every 2-3 years for optimal protection. New asphalt should cure 6-12 months before first sealcoating. Lincoln\'s freeze-thaw cycles and UV exposure break down asphalt binder, making regular sealcoating essential. Proper maintenance doubles pavement lifespan from 15 to 30+ years.',
    },
    {
      question: 'What does asphalt maintenance cost in Lincoln Nebraska?',
      answer: 'Sealcoating costs $0.15-$0.35 per square foot. Crack filling costs $1-$3 per linear foot. A typical 400 sq ft driveway costs $60-$140 to sealcoat and $50-$150 for crack filling. Preventive maintenance costs far less than repairs or replacement.',
    },
    {
      question: 'Can you do maintenance in winter?',
      answer: 'Sealcoating requires temperatures above 50°F and dry conditions. In Lincoln, we sealcoat April-October. Crack filling and pothole repairs can be done year-round with hot asphalt (above 50°F) or cold patch for emergency winter repairs. We schedule maintenance during optimal weather.',
    },
    {
      question: 'How long does sealcoating take to dry?',
      answer: 'Sealcoating dries in 24-48 hours depending on temperature and humidity. We recommend staying off for 24 hours minimum, 48 hours for heavy vehicles. Cooler temperatures and high humidity extend drying time. We schedule work to minimize disruption and ensure proper curing.',
    },
    {
      question: 'What\'s included in preventive maintenance?',
      answer: 'Our maintenance programs include annual inspections, crack filling, sealcoating every 2-3 years, minor repairs, and line striping as needed. Regular maintenance prevents 80% of major repairs. We create custom schedules based on traffic, climate exposure, and pavement condition.',
    },
    {
      question: 'Should I repair cracks before sealcoating?',
      answer: 'Yes! Crack filling before sealcoating is essential. Sealcoat won\'t fill cracks - it\'s too thin. We fill cracks with hot rubberized material, then sealcoat the entire surface. This combination provides maximum protection against water infiltration and pavement deterioration.',
    },
  ];

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: 'Inter, sans-serif' }}>
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-primary-500 text-sm mb-4 tracking-wider">Services / Maintenance</p>
            <h1 className="text-5xl md:text-6xl font-black mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Asphalt Maintenance Services
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              Professional sealcoating, crack filling, and pavement maintenance in Lincoln, Nebraska
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
            <div>
              <img
                src="https://public.readdy.ai/ai/img_res/c898a18968cb7a5c763517096912c07b.jpg"
                alt="Lincoln NE asphalt maintenance and sealcoating services"
                className="w-full h-96 object-cover rounded-2xl shadow-xl"
              />
            </div>
            <div>
              <h2 className="text-4xl font-bold mb-6 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Protect Your Pavement Investment
              </h2>
              <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                Regular asphalt maintenance is the key to maximizing pavement lifespan and minimizing costly repairs. Our comprehensive maintenance services protect your driveway or parking lot from Lincoln's harsh weather, UV damage, and daily wear.
              </p>
              <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                We specialize in sealcoating, crack filling, resurfacing, and preventive maintenance programs that keep your asphalt looking great and performing well for decades. Professional maintenance doubles pavement life from 15 to 30+ years while maintaining curb appeal.
              </p>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Serving Lincoln and Lancaster County for over 20 years, we provide expert maintenance services using premium materials and proven techniques. Our experienced crews work efficiently to minimize disruption while delivering lasting results.
              </p>
              <a href="tel:3084705637" className="inline-block bg-primary-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-primary-700 transition-colors cursor-pointer whitespace-nowrap">
                CALL (308) 470-5637
              </a>
            </div>
          </div>

          {/* Services Grid */}
          <div className="mb-16">
            <h2 className="text-4xl font-bold mb-12 text-center text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Maintenance Services
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {services.map((service, index) => (
                <div key={index} className="bg-gray-50 p-8 rounded-2xl border-2 border-gray-200 hover:border-primary-600 transition-all">
                  <div className="w-16 h-16 bg-primary-600 rounded-full flex items-center justify-center mb-6">
                    <i className={`${service.icon} text-3xl text-white`}></i>
                  </div>
                  <h3 className="text-2xl font-bold mb-4 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    {service.title}
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    {service.description}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Detailed Content */}
          <div className="prose prose-lg max-w-none mb-16">
            <h2 className="text-4xl font-bold mb-6 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Professional Asphalt Maintenance in Lincoln NE
            </h2>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Asphalt pavement is a significant investment that requires regular maintenance to perform well and last decades. Without proper care, Lincoln's extreme temperature swings, freeze-thaw cycles, and UV exposure rapidly deteriorate asphalt. Small cracks become potholes, minor surface wear becomes major structural failure, and a $200 maintenance service becomes a $5,000 replacement project.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Professional maintenance is cost-effective prevention. Regular sealcoating and crack filling protect asphalt from water infiltration, oxidation, and chemical damage. A well-maintained driveway lasts 30+ years versus 15 years for neglected pavement. The math is simple: spend $500 every few years on maintenance or $8,000 on premature replacement.
            </p>

            <h3 className="text-3xl font-bold mb-4 mt-8 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Asphalt Sealcoating Services
            </h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Sealcoating is the most important maintenance service for asphalt pavement. This protective coating shields asphalt from water, UV rays, chemicals, and oxidation that cause deterioration. Sealcoating fills surface voids, creates a smooth appearance, and extends pavement life by 5-10 years per application.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              We use premium coal tar or asphalt emulsion sealers formulated for Nebraska's climate. Our process includes thorough cleaning with power blowers, filling cracks with hot rubberized material, applying two coats of sealer with professional squeegees or spray equipment, and allowing proper curing time before traffic.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              New asphalt should cure 6-12 months before first sealcoating to allow oils to evaporate. After that, sealcoat every 2-3 years for optimal protection. Sealcoating costs $0.15-$0.35 per square foot - a typical 400 sq ft driveway costs $60-$140. This small investment prevents thousands in repair costs.
            </p>

            <h3 className="text-3xl font-bold mb-4 mt-8 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Crack Filling and Sealing
            </h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Cracks are asphalt's worst enemy. Even hairline cracks allow water infiltration that undermines the base and causes pavement failure. Lincoln's freeze-thaw cycles force water into cracks where it freezes, expands, and breaks apart the pavement. One winter can turn small cracks into major potholes.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              We use hot rubberized crack filler that remains flexible through temperature extremes. Our process includes cleaning cracks with compressed air or wire brushes, heating filler to 350-400°F, applying with professional equipment, and smoothing for a neat finish. For cracks wider than 1/2 inch, we route them out to create a reservoir for better adhesion.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Crack filling costs $1-$3 per linear foot and should be done every 2-3 years or whenever new cracks appear. Always fill cracks before sealcoating - sealcoat is too thin to fill cracks effectively. The combination of crack filling and sealcoating provides maximum protection.
            </p>

            <h3 className="text-3xl font-bold mb-4 mt-8 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Asphalt Resurfacing
            </h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Resurfacing (also called overlay) involves applying a new layer of asphalt over existing pavement. This is ideal when the surface is worn but the base remains stable. Resurfacing costs 50-70% less than complete replacement while providing a fresh, smooth surface that lasts 10-15 years.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              We mill the existing surface to create a bonding surface, repair any base problems, apply tack coat for adhesion, and pave with 1.5-2 inches of new asphalt. The result looks and performs like new pavement at a fraction of replacement cost. Resurfacing costs $2-$5 per square foot versus $7-$12 for complete replacement.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Resurfacing works best when damage is surface-level (oxidation, minor cracking, surface wear) rather than structural (alligator cracking, potholes, base failure). We assess your pavement and recommend resurfacing or replacement based on condition.
            </p>

            <h3 className="text-3xl font-bold mb-4 mt-8 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Pothole Repair and Patching
            </h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Potholes are safety hazards that damage vehicles and create liability. They form when water infiltrates through cracks, weakens the base, and traffic breaks apart the surface. We provide permanent pothole repairs using hot asphalt and proper techniques that last 5-10 years.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Our repair process: remove loose material, square off edges, clean thoroughly, apply tack coat, fill with hot asphalt in layers, compact each layer, and overfill slightly to account for settling. Proper compaction is critical - poorly compacted patches fail within months.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Hot asphalt repairs are permanent and cost $100-$300 per pothole. Cold patch is available for winter emergency repairs but is temporary (6-12 months). We return in spring to replace cold patches with permanent hot asphalt.
            </p>

            <h3 className="text-3xl font-bold mb-4 mt-8 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Parking Lot Striping
            </h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Professional line striping improves safety, maximizes parking capacity, and ensures ADA compliance. We provide complete striping services: parking spaces, fire lanes, handicap spaces, directional arrows, stop bars, and custom markings.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              We use high-quality traffic paint or thermoplastic that lasts 1-3 years depending on traffic. Our process includes layout planning, surface cleaning, precise application with professional equipment, and proper curing. Striping costs $0.15-$0.30 per linear foot for standard parking lots.
            </p>

            <h3 className="text-3xl font-bold mb-4 mt-8 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Preventive Maintenance Programs
            </h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Our preventive maintenance programs provide scheduled services that maximize pavement life and minimize costs. We create custom maintenance schedules based on pavement age, traffic, climate exposure, and condition. Regular inspections catch problems early when repairs are simple and inexpensive.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Typical programs include: annual inspections, crack filling as needed, sealcoating every 2-3 years, minor repairs, and line striping. Commercial properties benefit from multi-year contracts that budget maintenance costs and ensure timely service. Preventive maintenance prevents 80% of major repairs.
            </p>

            <h3 className="text-3xl font-bold mb-4 mt-8 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Why Choose Professional Maintenance
            </h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              DIY sealcoating products from big box stores seem economical but often cost more long-term. Consumer-grade sealers are thinner, less durable, and harder to apply properly. Poor application leaves streaks, thin spots, and inadequate protection. Professional equipment, materials, and expertise deliver superior results that last.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              We use commercial-grade materials, professional application equipment, and proven techniques developed over 20+ years. Our crews work efficiently to minimize disruption while delivering quality results. We stand behind our work with warranties and responsive service.
            </p>
          </div>

          {/* Before/After */}
          <div className="mb-16">
            <h2 className="text-4xl font-bold mb-12 text-center text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Maintenance Results
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <img
                  src="https://readdy.ai/api/search-image?query=worn%20faded%20asphalt%20driveway%20with%20cracks%20and%20oxidation%20before%20maintenance%20showing%20deteriorated%20gray%20surface%20in%20Lincoln%20Nebraska%20with%20clean%20simple%20light%20background&width=800&height=600&seq=102&orientation=landscape"
                  alt="Before asphalt maintenance"
                  className="w-full h-80 object-cover rounded-2xl shadow-xl"
                />
                <p className="text-center mt-4 text-xl font-semibold text-gray-700">Before Maintenance</p>
              </div>
              <div>
                <img
                  src="https://readdy.ai/api/search-image?query=freshly%20sealcoated%20asphalt%20driveway%20with%20deep%20black%20finish%20and%20sealed%20cracks%20showing%20professional%20maintenance%20results%20in%20Lincoln%20Nebraska%20with%20clean%20simple%20light%20background&width=800&height=600&seq=103&orientation=landscape"
                  alt="After asphalt maintenance"
                  className="w-full h-80 object-cover rounded-2xl shadow-xl"
                />
                <p className="text-center mt-4 text-xl font-semibold text-gray-700">After Maintenance</p>
              </div>
            </div>
          </div>

          {/* FAQs */}
          <div className="mb-16">
            <h2 className="text-4xl font-bold mb-12 text-center text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Frequently Asked Questions
            </h2>
            <div className="space-y-6">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-gray-50 p-8 rounded-2xl border-2 border-gray-200">
                  <h3 className="text-2xl font-bold mb-4 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    {faq.question}
                  </h3>
                  <p className="text-lg text-gray-700 leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Quote Form */}
          <div className="bg-gray-50 p-12 rounded-2xl">
            <h2 className="text-4xl font-bold mb-6 text-center text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Schedule Your Maintenance Service
            </h2>
            <p className="text-xl text-gray-700 text-center mb-8">
              Protect your pavement with professional maintenance services
            </p>
            <QuoteForm />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}